import { Region } from '../context/RegionContext';

export interface RegionalPricing {
  price: number;
  currency: string;
  symbol: string;
  exchangeRate: number;
  rateSource: 'online' | 'local';
}

export interface PricingResponse {
  variantId: string;
  movementType: string;
  region: string;
  regionalPricing: RegionalPricing;
}

export interface JewelleryPricingResponse {
  collectionId: string;
  itemId: string;
  region: string;
  regionalPricing: RegionalPricing;
}

export class PricingService {
  private static readonly API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private static priceCache = new Map<string, { data: PricingResponse; timestamp: number }>();
  private static jewelryPriceCache = new Map<string, { data: JewelleryPricingResponse; timestamp: number }>();

  /**
   * Get regional pricing for a watch variant
   */
  static async getRegionalPricing(
    variantId: string, 
    region: Region, 
    movementType: 'automatic_movement' | 'quartz_movement'
  ): Promise<PricingResponse> {
    const cacheKey = `${variantId}-${region}-${movementType}`;
    const cached = this.priceCache.get(cacheKey);
    
    // Return cached data if still valid
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const apiUrl = `${this.API_BASE_URL}/getRegionalPricing?variantId=${encodeURIComponent(variantId)}&region=${encodeURIComponent(region)}&movementType=${encodeURIComponent(movementType)}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Pricing API error: ${response.status} ${response.statusText}`);
      }

      const data: PricingResponse = await response.json();
      
      // Cache the response
      this.priceCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Failed to fetch regional pricing:', error);
      throw error;
    }
  }

  /**
   * Get regional pricing for a jewelry item
   */
  static async getJewelleryRegionalPricing(
    collectionId: string,
    itemId: string,
    region: Region
  ): Promise<JewelleryPricingResponse> {
    const cacheKey = `${collectionId}-${itemId}-${region}`;
    const cached = this.jewelryPriceCache.get(cacheKey);

    // Return cached data if still valid
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const apiUrl = `${this.API_BASE_URL}/getJewelleryRegionalPricing?collectionId=${encodeURIComponent(collectionId)}&itemId=${encodeURIComponent(itemId)}&region=${encodeURIComponent(region)}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Jewelry pricing API error: ${response.status} ${response.statusText}`);
      }

      const data: JewelleryPricingResponse = await response.json();

      // Cache the response
      this.jewelryPriceCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Failed to fetch jewelry regional pricing:', error);
      throw error;
    }
  }

  /**
   * Extract numeric price from various Euro price formats
   */
  static extractEuroPrice(priceValue: number | string | undefined): number {
    if (typeof priceValue === 'number') {
      return priceValue;
    }
    
    if (typeof priceValue === 'string') {
      // Remove currency symbols and spaces first
      let cleanPrice = priceValue.replace(/[€$£¥CHF\s]/g, '');
      
      // Handle European number format where dots are thousands separators
      // Examples: "5.990" = 5990, "25.880" = 25880, "1.234.567" = 1234567
      // Handle commas as either decimal separators or thousands separators
      if (cleanPrice.includes(',') && cleanPrice.split(',')[1]?.length <= 2) {
        // Comma is decimal separator: "5,99" -> "5.99"
        cleanPrice = cleanPrice.replace(/\./g, '').replace(',', '.');
      } else if (cleanPrice.includes(',')) {
        // Comma is thousands separator: "7,665" -> "7665"
        cleanPrice = cleanPrice.replace(/,/g, '');
      } else {
        // Dots are thousands separators: "5.990" -> "5990"
        cleanPrice = cleanPrice.replace(/\./g, '');
      }
      
      const numericValue = parseFloat(cleanPrice);
      return isNaN(numericValue) ? 0 : numericValue;
    }
    
    return 0;
  }

  /**
   * Check if price is available for a movement type
   */
  static isPriceAvailable(priceValue: number | string | undefined): boolean {
    if (!priceValue) return false;
    if (typeof priceValue === 'string' && priceValue.trim() === '') return false;
    if (typeof priceValue === 'string' && priceValue.toLowerCase().includes('on request')) return false;
    return this.extractEuroPrice(priceValue) > 0;
  }

  /**
   * Clear the pricing cache (useful for testing or when switching regions)
   */
  static clearCache(): void {
    this.priceCache.clear();
    this.jewelryPriceCache.clear();
  }

  /**
   * Format price fallback for when API fails
   */
  static formatFallbackPrice(priceValue: number | string | undefined, symbol: string = '€'): string {
    if (!priceValue) return '';
    
    if (typeof priceValue === 'string' && !this.isPriceAvailable(priceValue)) {
      return priceValue; // Return as-is for "on request" etc.
    }
    
    const numericPrice = this.extractEuroPrice(priceValue);
    if (numericPrice <= 0) return '';
    
    try {
      return `${symbol}${numericPrice.toLocaleString()}`;
    } catch (error) {
      return `${symbol}${numericPrice}`;
    }
  }
}